import { cn } from "@/lib/utils";

/**
 * Pipedrive's official monogram, verbatim from its press kit ("Logo/SVG/Pipedrive_Monogram_Green background.svg",
 * linked from https://www.pipedrive.com/en/newsroom/press-kit). Its own green disc and white p, never recolored,
 * so it reads on light and dark cards alike; size it like DealCommandCenterIcon, square, never stretched.
 *
 * Usage:
 *   <PipedriveMark className="size-3.5" />
 */
export function PipedriveMark({ className, ...props }: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 254 254" width="24" height="24" fill="none" aria-hidden="true" className={cn("shrink-0", className)} {...props}>
      <rect width="254" height="254" rx="127" fill="#017737" />
      <path
        d="M196.68 105.85C196.68 140.21 174.51 163.71 142.81 163.71C127.74 163.71 115.32 157.5 110.67 149.74L110.89 157.72V206.93H78.53V79.69C78.53 77.92 78.09 77.47 76.09 77.47H65V50.43H92.05C104.46 50.43 107.57 61.07 108.45 65.73C113.33 57.53 126.18 48 144.36 48C175.62 47.99 196.68 71.26 196.68 105.85ZM163.65 106.07C163.65 87.67 151.68 75.04 136.6 75.04C124.19 75.04 110.22 83.24 110.22 106.3C110.22 121.37 118.64 137.11 136.16 137.11C149.02 137.1 163.65 127.79 163.65 106.07Z"
        fill="white"
      />
    </svg>
  );
}

/**
 * ServiceTitan's "ST" monogram, the navy tile the feed and the record chips (`SourceMark`) both wear —
 * drawn as a path, not live text. Live text sits by the FONT's line box, not the tile's, so it lands
 * high and off-center and drifts with font/size; a path is identical at every size.
 *
 * The letterforms are the app's own UI font (IBM Plex Sans, Bold), outlined to a path with
 * `fontTools` (real curves on the S, no pixel-grid look) — never hand-drawn. Tracking is pulled in
 * ~8% tighter than the font's own advance width so "ST" reads as one mark. The combined glyph bounds
 * are centered in the 16×16 viewBox with an exact, computed margin on all four sides (not eyeballed),
 * so the optical centering holds regardless of render size.
 *
 * Usage:
 *   <ServiceTitanMark className="size-3.5" />
 */
export function ServiceTitanMark({ className, ...props }: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" className={cn("shrink-0", className)} {...props}>
      <rect width="16" height="16" rx="3" fill="#0B3A6E" />
      <path
        d="M5.416 11.5Q4.534 11.5 3.909 11.19Q3.283 10.88 2.847 10.404L3.816 9.425Q4.156 9.813 4.587 10.007Q5.019 10.201 5.513 10.201Q6.056 10.201 6.318 9.978Q6.58 9.755 6.58 9.386Q6.58 9.202 6.517 9.052Q6.454 8.902 6.284 8.8Q6.114 8.698 5.794 8.659L5.125 8.572Q4.418 8.475 3.957 8.218Q3.497 7.961 3.274 7.539Q3.051 7.118 3.051 6.555Q3.051 5.945 3.356 5.484Q3.661 5.024 4.229 4.762Q4.796 4.5 5.591 4.5Q6.357 4.5 6.938 4.747Q7.52 4.994 7.937 5.45L6.958 6.439Q6.715 6.158 6.371 5.979Q6.027 5.799 5.503 5.799Q5.009 5.799 4.767 5.974Q4.524 6.148 4.524 6.458Q4.524 6.691 4.607 6.837Q4.689 6.982 4.868 7.06Q5.048 7.137 5.329 7.186L5.998 7.292Q6.696 7.399 7.152 7.651Q7.607 7.903 7.83 8.31Q8.053 8.717 8.053 9.299Q8.053 9.949 7.748 10.443Q7.443 10.938 6.851 11.219Q6.26 11.5 5.416 11.5Z M13.153 5.925H11.321V11.384H9.847V5.925H8.015V4.616H13.153Z"
        fill="white"
      />
    </svg>
  );
}
